import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Checkbox } from '@/components/ui/checkbox';
import { 
  User, Building2, TrendingUp, Users, Trophy, Share2, Target, Globe, DollarSign,
  ArrowLeft, ArrowRight, CheckCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { z } from 'zod';
import { cn } from '@/lib/utils';

const baseSchema = z.object({
  firstName: z.string().min(1, 'Required').max(100),
  lastName: z.string().min(1, 'Required').max(100),
  email: z.string().email('Invalid email').max(255),
  password: z.string()
    .min(8, 'Password must be at least 8 characters')
    .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
    .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
    .regex(/[0-9]/, 'Password must contain at least one number'),
  confirmPassword: z.string(),
  phoneNumber: z.string().min(1, 'Phone number is required'),
  accountType: z.enum(['talent', 'agent', 'brand', 'venture']),
  referralSource: z.string().optional(),
  agreedToTerms: z.boolean().refine(val => val === true, 'Must agree to terms'),
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
});

interface ComprehensiveBetaFormProps {
  onSubmit: (data: any) => Promise<void>;
  isSubmitting: boolean;
}

export function ComprehensiveBetaForm({ onSubmit, isSubmitting }: ComprehensiveBetaFormProps) {
  const [currentStep, setCurrentStep] = useState(1);
  const [accountType, setAccountType] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  // Basic info
  const [basicInfo, setBasicInfo] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    phoneNumber: '',
    referralSource: '',
    agreedToTerms: false,
  });

  // Talent-specific fields
  const [talentInfo, setTalentInfo] = useState({
    name: '',
    sport: '',
    talentType: 'athlete',
    bio: '',
    location: '',
    instagram: '',
    twitter: '',
    tiktok: '',
    youtube: '',
    achievements: [] as string[],
    status: 'Active',
  });

  const totalSteps = accountType ? getTotalStepsForType(accountType) : 3;
  const progress = (currentStep / totalSteps) * 100;

  function getTotalStepsForType(type: string) {
    switch (type) {
      case 'talent':
        return 5; // Basic, Type, Talent Info, Social, Review
      case 'agent':
      case 'brand':
      case 'venture':
        return 4; // Simplified for demo
      default:
        return 3;
    }
  }

  const handleNext = () => {
    if (currentStep === 2 && !accountType) {
      toast.error('Please select an account type');
      return;
    }
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrev = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async () => {
    // Validate
    setFieldErrors({});
    
    const validation = baseSchema.safeParse({
      ...basicInfo,
      accountType: accountType as string,
    });

    if (!validation.success) {
      const errors: Record<string, string> = {};
      validation.error.errors.forEach((err) => {
        const field = err.path[0] as string;
        errors[field] = err.message;
      });
      setFieldErrors(errors);
      
      const firstError = validation.error.errors[0];
      toast.error(firstError?.message || 'Please check your information');
      
      // Scroll to first step with errors
      if (errors.firstName || errors.lastName || errors.email || 
          errors.password || errors.confirmPassword ||
          errors.phoneNumber) {
        setCurrentStep(1);
      }
      return;
    }

    // Prepare data based on account type
    const submissionData: any = {
      firstName: basicInfo.firstName,
      lastName: basicInfo.lastName,
      first_name: basicInfo.firstName,
      last_name: basicInfo.lastName,
      email: basicInfo.email,
      password: basicInfo.password,
      confirmPassword: basicInfo.confirmPassword,
      phone_number: basicInfo.phoneNumber,
      account_type_interest: accountType,
      referral_source: basicInfo.referralSource || null,
      display_name: `${basicInfo.firstName} ${basicInfo.lastName}`,
      status: 'pending',
    };

    if (accountType === 'talent') {
      submissionData.talent_name = talentInfo.name;
      submissionData.talent_sport = talentInfo.sport;
      submissionData.talent_type = talentInfo.talentType;
      submissionData.talent_bio = talentInfo.bio;
      submissionData.talent_location = talentInfo.location;
      submissionData.talent_social_profiles = {
        instagram: talentInfo.instagram,
        twitter: talentInfo.twitter,
        tiktok: talentInfo.tiktok,
        youtube: talentInfo.youtube,
      };
      submissionData.talent_achievements = talentInfo.achievements;
      submissionData.talent_status = talentInfo.status;
    }

    await onSubmit(submissionData);
  };

  const renderStepContent = () => {
    // Step 1: Basic Info
    if (currentStep === 1) {
      return (
        <div className="space-y-6">
          <div className="text-center mb-6">
            <User className="h-12 w-12 text-primary mx-auto mb-2" />
            <h2 className="text-2xl font-bold">Basic Information</h2>
            <p className="text-muted-foreground">Let's start with your details</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="firstName">First Name *</Label>
              <Input
                id="firstName"
                value={basicInfo.firstName}
                onChange={(e) => {
                  setBasicInfo({ ...basicInfo, firstName: e.target.value });
                  if (fieldErrors.firstName) {
                    setFieldErrors({ ...fieldErrors, firstName: '' });
                  }
                }}
                placeholder="John"
                className={cn(fieldErrors.firstName && "border-destructive")}
              />
              {fieldErrors.firstName && (
                <p className="text-xs text-destructive">{fieldErrors.firstName}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="lastName">Last Name *</Label>
              <Input
                id="lastName"
                value={basicInfo.lastName}
                onChange={(e) => {
                  setBasicInfo({ ...basicInfo, lastName: e.target.value });
                  if (fieldErrors.lastName) {
                    setFieldErrors({ ...fieldErrors, lastName: '' });
                  }
                }}
                placeholder="Doe"
                className={cn(fieldErrors.lastName && "border-destructive")}
              />
              {fieldErrors.lastName && (
                <p className="text-xs text-destructive">{fieldErrors.lastName}</p>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="email">Email *</Label>
            <Input
              id="email"
              type="email"
              value={basicInfo.email}
              onChange={(e) => {
                setBasicInfo({ ...basicInfo, email: e.target.value });
                if (fieldErrors.email) {
                  setFieldErrors({ ...fieldErrors, email: '' });
                }
              }}
              placeholder="john@example.com"
              className={cn(fieldErrors.email && "border-destructive")}
            />
            {fieldErrors.email && (
              <p className="text-xs text-destructive">{fieldErrors.email}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="password">Password *</Label>
            <Input
              id="password"
              type="password"
              value={basicInfo.password}
              onChange={(e) => {
                setBasicInfo({ ...basicInfo, password: e.target.value });
                if (fieldErrors.password) {
                  setFieldErrors({ ...fieldErrors, password: '' });
                }
              }}
              placeholder="Enter a secure password"
              className={cn(fieldErrors.password && "border-destructive")}
            />
            {fieldErrors.password && (
              <p className="text-xs text-destructive">{fieldErrors.password}</p>
            )}
            <p className="text-xs text-muted-foreground">
              At least 8 characters with uppercase, lowercase, and number
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="confirmPassword">Confirm Password *</Label>
            <Input
              id="confirmPassword"
              type="password"
              value={basicInfo.confirmPassword}
              onChange={(e) => {
                setBasicInfo({ ...basicInfo, confirmPassword: e.target.value });
                if (fieldErrors.confirmPassword) {
                  setFieldErrors({ ...fieldErrors, confirmPassword: '' });
                }
              }}
              placeholder="Re-enter your password"
              className={cn(fieldErrors.confirmPassword && "border-destructive")}
            />
            {fieldErrors.confirmPassword && (
              <p className="text-xs text-destructive">{fieldErrors.confirmPassword}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="phoneNumber">Phone Number *</Label>
            <Input
              id="phoneNumber"
              type="tel"
              value={basicInfo.phoneNumber}
              onChange={(e) => {
                setBasicInfo({ ...basicInfo, phoneNumber: e.target.value });
                if (fieldErrors.phoneNumber) {
                  setFieldErrors({ ...fieldErrors, phoneNumber: '' });
                }
              }}
              placeholder="(555) 123-4567"
              className={cn(fieldErrors.phoneNumber && "border-destructive")}
            />
            {fieldErrors.phoneNumber && (
              <p className="text-xs text-destructive">{fieldErrors.phoneNumber}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="referralSource">How did you hear about us?</Label>
            <Select value={basicInfo.referralSource} onValueChange={(value) => setBasicInfo({ ...basicInfo, referralSource: value })}>
              <SelectTrigger>
                <SelectValue placeholder="Select source" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="search">Search Engine</SelectItem>
                <SelectItem value="social">Social Media</SelectItem>
                <SelectItem value="referral">Referral</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      );
    }

    // Step 2: Account Type Selection
    if (currentStep === 2) {
      return (
        <div className="space-y-6">
          <div className="text-center mb-6">
            <Users className="h-12 w-12 text-primary mx-auto mb-2" />
            <h2 className="text-2xl font-bold">Select Account Type</h2>
            <p className="text-muted-foreground">Choose what best describes you</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {[
              { value: 'talent', icon: Trophy, title: 'Talent', desc: 'Athletes, creators, entertainers' },
              { value: 'agent', icon: Users, title: 'Agent', desc: 'Talent representatives' },
              { value: 'brand', icon: Building2, title: 'Brand', desc: 'Companies seeking partnerships' },
              { value: 'venture', icon: TrendingUp, title: 'Venture Capital', desc: 'Investors and VCs' },
            ].map((option) => (
              <Card
                key={option.value}
                className={`cursor-pointer transition-all hover:border-primary ${
                  accountType === option.value ? 'border-primary bg-primary/5' : ''
                }`}
                onClick={() => setAccountType(option.value)}
              >
                <CardContent className="pt-6">
                  <option.icon className="h-8 w-8 text-primary mb-2" />
                  <h3 className="font-semibold mb-1">{option.title}</h3>
                  <p className="text-sm text-muted-foreground">{option.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      );
    }

    // Account type-specific steps
    if (accountType === 'talent') {
      if (currentStep === 3) {
        return (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <Trophy className="h-12 w-12 text-primary mx-auto mb-2" />
              <h2 className="text-2xl font-bold">Your Talent Profile</h2>
              <p className="text-muted-foreground">Tell us about your career</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="talentName">Full Name *</Label>
              <Input
                id="talentName"
                value={talentInfo.name}
                onChange={(e) => setTalentInfo({ ...talentInfo, name: e.target.value })}
                placeholder="Your professional name"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="talentType">I am a *</Label>
              <Select value={talentInfo.talentType} onValueChange={(value) => setTalentInfo({ ...talentInfo, talentType: value })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="athlete">Athlete</SelectItem>
                  <SelectItem value="creator">Content Creator</SelectItem>
                  <SelectItem value="entertainer">Entertainer</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="sport">Sport/Field *</Label>
              <Input
                id="sport"
                value={talentInfo.sport}
                onChange={(e) => setTalentInfo({ ...talentInfo, sport: e.target.value })}
                placeholder="e.g., Tennis, Content Creation"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="bio">Bio *</Label>
              <Textarea
                id="bio"
                value={talentInfo.bio}
                onChange={(e) => setTalentInfo({ ...talentInfo, bio: e.target.value })}
                placeholder="Tell us about yourself..."
                rows={4}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="location">Location</Label>
              <Input
                id="location"
                value={talentInfo.location}
                onChange={(e) => setTalentInfo({ ...talentInfo, location: e.target.value })}
                placeholder="e.g., Los Angeles, CA"
              />
            </div>
          </div>
        );
      }

      if (currentStep === 4) {
        return (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <Share2 className="h-12 w-12 text-primary mx-auto mb-2" />
              <h2 className="text-2xl font-bold">Social Media</h2>
              <p className="text-muted-foreground">Connect your profiles</p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[
                { id: 'instagram', label: 'Instagram', placeholder: '@username' },
                { id: 'twitter', label: 'Twitter/X', placeholder: '@username' },
                { id: 'tiktok', label: 'TikTok', placeholder: '@username' },
                { id: 'youtube', label: 'YouTube', placeholder: 'Channel URL' },
              ].map((social) => (
                <div key={social.id} className="space-y-2">
                  <Label htmlFor={social.id}>{social.label}</Label>
                  <Input
                    id={social.id}
                    value={talentInfo[social.id as keyof typeof talentInfo] as string}
                    onChange={(e) => setTalentInfo({ ...talentInfo, [social.id]: e.target.value })}
                    placeholder={social.placeholder}
                  />
                </div>
              ))}
            </div>
          </div>
        );
      }

      if (currentStep === 5) {
        return (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <CheckCircle className="h-12 w-12 text-primary mx-auto mb-2" />
              <h2 className="text-2xl font-bold">Review Application</h2>
              <p className="text-muted-foreground">Please review before submitting</p>
            </div>

            <Card>
              <CardContent className="pt-6 space-y-4">
                <div>
                  <h3 className="font-semibold text-lg mb-2">{talentInfo.name}</h3>
                  <p className="text-sm text-muted-foreground">{talentInfo.sport} • {talentInfo.talentType}</p>
                </div>
                <div>
                  <h4 className="font-medium mb-2">Bio</h4>
                  <p className="text-sm text-muted-foreground">{talentInfo.bio}</p>
                </div>
                {talentInfo.location && (
                  <div>
                    <h4 className="font-medium mb-2">Location</h4>
                    <p className="text-sm text-muted-foreground">{talentInfo.location}</p>
                  </div>
                )}
              </CardContent>
            </Card>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="terms"
                checked={basicInfo.agreedToTerms}
                onCheckedChange={(checked) => setBasicInfo({ ...basicInfo, agreedToTerms: !!checked })}
              />
              <label htmlFor="terms" className="text-sm">
                I agree to the Terms of Service and Privacy Policy
              </label>
            </div>
          </div>
        );
      }
    }

    // Simplified for other account types
    if (accountType && currentStep === 3) {
      return (
        <div className="space-y-6">
          <div className="text-center mb-6">
            <CheckCircle className="h-12 w-12 text-primary mx-auto mb-2" />
            <h2 className="text-2xl font-bold">Almost There!</h2>
            <p className="text-muted-foreground">
              Additional fields for {accountType} accounts will be collected after approval
            </p>
          </div>

          <div className="flex items-center space-x-2">
            <Checkbox
              id="terms"
              checked={basicInfo.agreedToTerms}
              onCheckedChange={(checked) => setBasicInfo({ ...basicInfo, agreedToTerms: !!checked })}
            />
            <label htmlFor="terms" className="text-sm">
              I agree to the Terms of Service and Privacy Policy
            </label>
          </div>
        </div>
      );
    }

    return null;
  };

  return (
    <div className="space-y-6">
      <div className="mb-8">
        <div className="flex justify-between items-center mb-2">
          <span className="text-sm text-muted-foreground">
            Step {currentStep} of {totalSteps}
          </span>
          <span className="text-sm font-medium">{Math.round(progress)}%</span>
        </div>
        <Progress value={progress} className="h-2" />
      </div>

      {renderStepContent()}

      <div className="flex justify-between mt-8">
        <Button
          variant="outline"
          onClick={handlePrev}
          disabled={currentStep === 1 || isSubmitting}
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Previous
        </Button>

        {currentStep === totalSteps ? (
          <Button
            onClick={handleSubmit}
            disabled={isSubmitting || !basicInfo.agreedToTerms}
          >
            {isSubmitting ? 'Submitting...' : 'Submit Application'}
            <CheckCircle className="h-4 w-4 ml-2" />
          </Button>
        ) : (
          <Button onClick={handleNext}>
            Next
            <ArrowRight className="h-4 w-4 ml-2" />
          </Button>
        )}
      </div>
    </div>
  );
}
